import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { getPctColorHex } from '@/lib/statColors';
import { ArrowLeft, Link2, X, Plus, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import GameAnalysisModal from '@/components/GameAnalysisModal';

/* ──────────────────────────────────────────────────────────────────────────
   Player Stats Card
   • Pulls eligible games (MY_PLAYER + BOTH_TEAMS) for the current team.
   • Aggregates the player's box scores across those games.
   • Layout mirrors design mock: navy header, hero PPG, splits, game log.
   ────────────────────────────────────────────────────────────────────────── */

const NAVY = '#0B1824';
const ORANGE = '#e8963a';
const MUTED = '#8FA3B1';
const BORDER = '#DDE5EC';

/* Shooting-split colours live in /app/frontend/src/lib/statColors.js
   (build 210). The same `getPctColorHex` helper backs GameSummary and
   PlayerProfile so the green/yellow/red tiers stay consistent across
   every surface. Local 'splitColor' is a thin adapter that maps the
   UI's tile label ('2pt' / '3pt' / 'ft' / 'fg' / 'efg' / 'ts') to the
   tier-key the shared helper understands. 2PT% has no separate tier
   so we score it on the FG% scale (closest semantic match). */
function splitColor(stat, value) {
  const type = stat === '2pt' ? 'fg' : stat;
  return getPctColorHex(value, undefined, type);
}

const fmtAvg = (n, d) => (d > 0 ? (n / d).toFixed(1) : null);
const fmtPct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : null);

export default function PlayerStats() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { selectedTeamId, teams = [] } = useOutletContext() || {};
  const [filter, setFilter] = useState('last5');
  const [player, setPlayer] = useState(null);
  const [games, setGames] = useState([]);            // eligible chronological
  const [boxRows, setBoxRows] = useState({});        // game_id -> player's row in that game
  const [loading, setLoading] = useState(true);
  const [analysisGame, setAnalysisGame] = useState(null);  // {game, row} when the analysis modal is open

  // Linked players: codes from other coaches that the user has linked to
  // this profile. Their game logs + box scores get unioned into the same
  // aggregation so this profile shows the full cross-team picture.
  const [links, setLinks] = useState([]);                // [{ player_id, name, team_name, link_code, jersey_number }]
  const [linksModalOpen, setLinksModalOpen] = useState(false);
  const [linkingCode, setLinkingCode] = useState('');
  const [linking, setLinking] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null); // a link object pending disconnect confirmation
  const [linksDirty, setLinksDirty] = useState(0);       // bump to refetch after add/remove

  const team = teams.find(t => t.team_id === selectedTeamId);

  /* ── Fetch player profile + eligible games + per-game box scores ──
        Also pulls any linked players (via player codes) and unions
        their game/box-score data into the same aggregation. Each row
        in `boxRows` is keyed by a synthetic `_key` so two players
        sharing a `game_id` (rare, but possible if a kid plays in a
        scrimmage tracked by both teams) don't clobber each other. */
  useEffect(() => {
    if (!selectedTeamId || !playerId) return;
    setLoading(true);
    (async () => {
      try {
        const [rosterRes, gamesRes, linksRes] = await Promise.all([
          api.get(`/teams/${selectedTeamId}/players`),
          api.get(`/teams/${selectedTeamId}/games`),
          api.get(`/players/${playerId}/links`).catch(() => ({ data: { links: [] } })),
        ]);
        const roster = rosterRes.data || [];
        const me = roster.find(p => p.player_id === playerId);
        setPlayer(me || null);

        const linked = linksRes.data?.links || [];
        setLinks(linked);

        // Eligible games for the CURRENT-TEAM profile
        const eligible = (gamesRes.data || [])
          .filter(g => (g.status === 'final' || g.status === 'locked'))
          .filter(g => g.tracking_mode === 'MY_PLAYER' || g.tracking_mode === 'BOTH_TEAMS')
          .sort((a, b) => new Date(b.date_time) - new Date(a.date_time));

        const rows = {};
        const tagged = [];

        // Current-team box scores keyed by game_id.
        // CRITICAL: the box-scores endpoint returns the team's stats
        // in `home` when we played at home and `away` when we played
        // away. Always pick the bucket matching g.is_home — reading
        // `home` unconditionally was the bug that hid every away-game
        // player aggregate, imports and live tracks alike.
        await Promise.all(eligible.map(async g => {
          try {
            const r = await api.get(`/games/${g.game_id}/box-scores`);
            const ourSide = g.is_home ? r.data?.home : r.data?.away;
            const players = ourSide?.players || [];
            const row = players.find(p => p.player_id === playerId);
            if (row) {
              const _key = g.game_id;
              rows[_key] = row;
              tagged.push({ ...g, _key });
            }
          } catch { /* ignore */ }
        }));

        // Linked players' games + box scores via the dedicated endpoint
        // (authorized server-side because a link exists).
        await Promise.all(linked.map(async (lp) => {
          try {
            const r = await api.get(`/players/${lp.player_id}/linked-games`);
            const lg = r.data?.games || [];
            for (const g of lg) {
              const _key = `${lp.player_id}::${g.game_id}`;
              rows[_key] = g.box_row;
              tagged.push({ ...g, _key, _linked_team_name: g.team_name || lp.team_name });
            }
          } catch { /* ignore */ }
        }));

        const merged = tagged.sort(
          (a, b) => new Date(b.date_time) - new Date(a.date_time)
        );
        setGames(merged);
        setBoxRows(rows);
      } finally { setLoading(false); }
    })();
  }, [selectedTeamId, playerId, linksDirty]);

  /* ── Window filter ── */
  const filteredGames = useMemo(() => {
    const withRow = games.filter(g => boxRows[g._key]);
    if (filter === 'last5')  return withRow.slice(0, 5);
    if (filter === 'last10') return withRow.slice(0, 10);
    return withRow; // all
  }, [games, boxRows, filter]);

  /* ── Aggregate ── */
  const agg = useMemo(() => {
    const z = { gp: 0, pts:0, reb:0, oreb:0, dreb:0, ast:0, stl:0, blk:0, defl:0, to:0, pf:0,
                fgm:0, fga:0, three_pm:0, three_pa:0, ftm:0, fta:0, two_pm:0, two_pa:0 };
    for (const g of filteredGames) {
      const r = boxRows[g._key]; if (!r) continue;
      z.gp += 1;
      z.pts += r.pts || 0;
      z.reb += r.reb || ((r.oreb || 0) + (r.dreb || 0));
      z.oreb += r.oreb || 0;
      z.dreb += r.dreb || 0;
      z.ast += r.ast || 0;
      z.stl += r.stl || 0;
      z.blk += r.blk || 0;
      z.defl += r.defl || 0;
      z.to += r.to || 0;
      z.pf += r.pf || 0;
      z.fgm += r.fgm || 0;
      z.fga += r.fga || 0;
      z.three_pm += r.three_pm || 0;
      z.three_pa += r.three_pa || 0;
      z.ftm += r.ftm || 0;
      z.fta += r.fta || 0;
      z.two_pm += (r.two_pm != null ? r.two_pm : (r.fgm || 0) - (r.three_pm || 0));
      z.two_pa += (r.two_pa != null ? r.two_pa : (r.fga || 0) - (r.three_pa || 0));
    }
    return z;
  }, [filteredGames, boxRows]);

  /* ── Per-game averages ── */
  const perGame = useMemo(() => ({
    pts:  fmtAvg(agg.pts, agg.gp),
    reb:  fmtAvg(agg.reb, agg.gp),
    ast:  fmtAvg(agg.ast, agg.gp),
    stl:  fmtAvg(agg.stl, agg.gp),
    blk:  fmtAvg(agg.blk, agg.gp),
    defl: fmtAvg(agg.defl, agg.gp),
    oreb: fmtAvg(agg.oreb, agg.gp),
    dreb: fmtAvg(agg.dreb, agg.gp),
    to:   fmtAvg(agg.to, agg.gp),
    pf:   fmtAvg(agg.pf, agg.gp),
  }), [agg]);

  /* ── Shooting splits ── */
  const splits = useMemo(() => {
    // True Shooting % = pts / (2 * (FGA + 0.44 * FTA)). Returns null when
    // the denominator is zero so the tile renders NA.
    const tsDenom = 2 * (agg.fga + 0.44 * agg.fta);
    const tsPct = tsDenom > 0 ? Math.round((agg.pts / tsDenom) * 100) : null;
    return {
      twoPct:   fmtPct(agg.two_pm,   agg.two_pa),
      threePct: fmtPct(agg.three_pm, agg.three_pa),
      ftPct:    fmtPct(agg.ftm,      agg.fta),
      fgPct:    fmtPct(agg.fgm,      agg.fga),
      eFG:      agg.fga > 0 ? Math.round(((agg.fgm + 0.5 * agg.three_pm) / agg.fga) * 100) : null,
      tsPct,
    };
  }, [agg]);

  /* ── All-time aggregate (ignores Last 5 / Last 10 / All filter).
        Used as the comparison baseline for the per-game analysis
        modal — comparing a game against an averaged window that
        already includes itself would be circular. ── */
  const allTimeAgg = useMemo(() => {
    const z = { gp: 0, pts:0, reb:0, oreb:0, dreb:0, ast:0, stl:0, blk:0, defl:0, to:0, pf:0,
                fgm:0, fga:0, three_pm:0, three_pa:0, ftm:0, fta:0, two_pm:0, two_pa:0 };
    for (const g of games) {
      const r = boxRows[g._key]; if (!r) continue;
      z.gp += 1;
      z.pts += r.pts || 0;
      z.reb += r.reb || ((r.oreb || 0) + (r.dreb || 0));
      z.oreb += r.oreb || 0;
      z.dreb += r.dreb || 0;
      z.ast += r.ast || 0;
      z.stl += r.stl || 0;
      z.blk += r.blk || 0;
      z.defl += r.defl || 0;
      z.to += r.to || 0;
      z.pf += r.pf || 0;
      z.fgm += r.fgm || 0;
      z.fga += r.fga || 0;
      z.three_pm += r.three_pm || 0;
      z.three_pa += r.three_pa || 0;
      z.ftm += r.ftm || 0;
      z.fta += r.fta || 0;
      z.two_pm += (r.two_pm != null ? r.two_pm : (r.fgm || 0) - (r.three_pm || 0));
      z.two_pa += (r.two_pa != null ? r.two_pa : (r.fga || 0) - (r.three_pa || 0));
    }
    return z;
  }, [games, boxRows]);

  /* ── ATS shape consumed by GameAnalysisModal.
        `averages` holds the per-game means; `totals` holds raw sums
        so the modal can derive 2PT% if averages.two_pct is missing. ── */
  const ats = useMemo(() => {
    const a = allTimeAgg;
    const num = (val, denom) => (denom > 0 ? Math.round((val / denom) * 10) / 10 : null);
    const pct = (val, denom) => (denom > 0 ? Math.round((val / denom) * 100 * 10) / 10 : null);
    return {
      averages: {
        ppg:        num(a.pts, a.gp),
        rpg:        num(a.reb, a.gp),
        apg:        num(a.ast, a.gp),
        spg:        num(a.stl, a.gp),
        bpg:        num(a.blk, a.gp),
        topg:       num(a.to, a.gp),
        two_pct:    pct(a.two_pm, a.two_pa),
        three_p_pct:pct(a.three_pm, a.three_pa),
        ft_pct:     pct(a.ftm, a.fta),
        fg_pct:     pct(a.fgm, a.fga),
      },
      totals: {
        fgm: a.fgm, fga: a.fga,
        three_pm: a.three_pm, three_pa: a.three_pa,
        ftm: a.ftm, fta: a.fta,
      },
    };
  }, [allTimeAgg]);

  const totalTracked = Object.keys(boxRows).length;

  /* ── Link management actions ── */
  const submitLinkCode = async () => {
    const code = (linkingCode || '').trim().toUpperCase();
    if (!/^[A-Z0-9]{8}$/.test(code)) {
      toast.error('Enter the 8-character code (A–Z, 0–9).');
      return;
    }
    setLinking(true);
    try {
      await api.post(`/players/${playerId}/link`, { code });
      toast.success('Player linked. Stats updated.');
      setLinkingCode('');
      setLinksDirty(v => v + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not link that code.');
    } finally {
      setLinking(false);
    }
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget) return;
    try {
      await api.delete(`/players/${playerId}/link/${unlinkTarget.player_id}`);
      toast.success('Disconnected');
      setUnlinkTarget(null);
      setLinksDirty(v => v + 1);
    } catch {
      toast.error('Could not disconnect');
    }
  };

  /* ── Render ── */
  return (
    <div className="-mx-4 sm:-mx-6 lg:mx-0 lg:max-w-2xl" data-testid="player-stats-page">
      {/* Back nav lives above the navy block on a neutral strip — keeps
          the player block focused on identity, mirroring the consolidated
          Player Profile view. */}
      <div className="px-4 py-2.5"
        style={{ background: '#fff', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
        <button
          onClick={() => navigate('/stats')}
          data-testid="player-stats-back"
          className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[.06em]"
          style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", background: 'none', border: 'none' }}
        >
          <ArrowLeft className="w-4 h-4" /> Player Stats
        </button>
      </div>

      {/* HEADER — single row matching the Player Profile shell layout:
          avatar | name + team + badge. */}
      <div className="px-4 pt-5 pb-5"
        style={{ background: NAVY, borderBottom: `2px solid ${ORANGE}` }}>
        <div className="flex items-center gap-3.5">
          <div className="w-[68px] h-[68px] rounded-full flex items-center justify-center shrink-0"
            style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 33 }}
            data-testid="player-jersey-avatar">
            {player?.jersey_number || '–'}
          </div>

          <div className="flex-1">
            <p
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '.01em', fontSize: 27 }}
              data-testid="player-name">
              {(player?.name || 'PLAYER').toUpperCase()}
            </p>
            <p className="mt-1" style={{ color: 'rgba(255,255,255,.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.06em', fontWeight: 700, fontSize: 15, whiteSpace: 'normal' }}>
              {(team?.name || '').toUpperCase()}{team?.level ? ` · ${team.level.toUpperCase()}` : ''}
            </p>
            <span className="inline-block mt-1.5 px-2.5 py-0.5 rounded-full whitespace-nowrap"
              style={{ background: 'rgba(255,255,255,.1)', color: 'rgba(255,255,255,.65)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.08em', fontSize: 13 }}
              data-testid="player-games-tracked-badge">
              {totalTracked} GAME{totalTracked === 1 ? '' : 'S'} TRACKED
            </span>
          </div>
        </div>
      </div>

      {/* ── FILTER PILLS ── */}
      <div className="px-3 py-3 flex gap-2"
        style={{ background: '#fff', borderBottom: '1px solid rgba(0,0,0,.06)' }}
        data-testid="player-filters">
        {[
          { k: 'last5',  label: 'Last 5' },
          { k: 'last10', label: 'Last 10' },
          { k: 'all',    label: 'All Time' },
        ].map(f => (
          <button key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`player-filter-${f.k}`}
            className="h-10 px-4 rounded-full text-[13px] font-bold uppercase tracking-[.06em] transition-all"
            style={{
              background: filter === f.k ? NAVY : '#F1F5F9',
              color: filter === f.k ? '#fff' : 'rgba(0,0,0,.45)',
              border: `1px solid ${filter === f.k ? NAVY : 'rgba(0,0,0,.1)'}`,
              fontFamily: "'Barlow Condensed', sans-serif",
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── PER GAME AVERAGES ── */}
      <Section title="Per Game Averages" testid="section-per-game">
        {loading ? <p className="text-[13px]" style={{ color: MUTED }}>Loading…</p> : (
        <>
          <Row5
            cells={[
              { label: 'PTS',  val: perGame.pts,  highlight: true },
              { label: 'REB',  val: perGame.reb },
              { label: 'AST',  val: perGame.ast },
              { label: 'STL',  val: perGame.stl },
              { label: 'BLK',  val: perGame.blk },
            ]}
          />
          <div className="h-2.5" />
          <Row5
            cells={[
              { label: 'DEFL', val: perGame.defl },
              { label: 'OREB', val: perGame.oreb },
              { label: 'DREB', val: perGame.dreb },
              { label: 'TO',   val: perGame.to },
              { label: 'FOUL', val: perGame.pf },
            ]}
          />
        </>)}
      </Section>

      {/* ── SHOOTING SPLITS ── */}
      <Section title="Shooting Splits" testid="section-splits">
        <div className="grid grid-cols-3 gap-2">
          <SplitTile label="2PT%" pct={splits.twoPct}   color={splitColor('2pt', splits.twoPct)}   />
          <SplitTile label="3PT%" pct={splits.threePct} color={splitColor('3pt', splits.threePct)} />
          <SplitTile label="FT%"  pct={splits.ftPct}    color={splitColor('ft',  splits.ftPct)}    />
          <SplitTile label="FG%"  pct={splits.fgPct}    color={splitColor('fg',  splits.fgPct)}    />
          <SplitTile label="eFG%" pct={splits.eFG}      color={splitColor('efg', splits.eFG)}      />
          <SplitTile label="TS%"  pct={splits.tsPct}    color={splitColor('ts',  splits.tsPct)}    />
        </div>
      </Section>

      {/* ── GAME LOG ── */}
      <Section title="Game Log" testid="section-gamelog">
        {filteredGames.length === 0 ? (
          <p className="text-[13px]" style={{ color: MUTED }}>No games tracked yet for this player.</p>
        ) : (
          filteredGames.map(g => {
            const r = boxRows[g._key]; if (!r) return null;
            const won = g.is_home ? (g.home_score >= g.away_score) : (g.away_score >= g.home_score);
            const us = g.is_home ? g.home_score : g.away_score;
            const them = g.is_home ? g.away_score : g.home_score;
            const dateStr = g.date_time ? new Date(g.date_time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
            const oppName = (g.opponent_name || 'Opponent').toUpperCase();
            return (
              <div
                key={g._key}
                data-testid={`player-game-row-${g._key}`}
                className="py-3"
                style={{ borderBottom: '1px solid rgba(0,0,0,.06)' }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] truncate"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: NAVY }}>
                      vs {oppName}
                    </p>
                    <p className="text-[14px] mt-0.5" style={{ color: 'rgba(0,0,0,.45)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 }}>
                      {dateStr} · {won ? 'W' : 'L'} {us}-{them}
                    </p>
                  </div>
                  <div className="flex items-end gap-4 shrink-0">
                    {[
                      { label: 'PTS', val: r.pts || 0 },
                      { label: 'REB', val: r.reb != null ? r.reb : ((r.oreb || 0) + (r.dreb || 0)) },
                      { label: 'AST', val: r.ast || 0 },
                    ].map(s => (
                      <div key={s.label} className="text-center">
                        <div className="text-[18px] leading-none"
                          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: NAVY }}>
                          {s.val}
                        </div>
                        <div className="text-[11px] mt-1"
                          style={{ color: 'rgba(0,0,0,.4)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.06em' }}>
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Game Breakdown CTA — mirrors the Player Profile shell
                    so both stat surfaces feel identical. */}
                <button
                  type="button"
                  onClick={() => setAnalysisGame({ game: g, row: r })}
                  data-testid={`player-game-breakdown-${g._key}`}
                  className="mt-2 ml-auto flex items-center gap-1 h-8 px-3 rounded-full transition-all active:scale-[.97]"
                  style={{
                    background: '#F1F5F9',
                    color: NAVY,
                    border: `1px solid ${BORDER}`,
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 800,
                    fontSize: 11,
                    letterSpacing: '.10em',
                  }}
                >
                  GAME BREAKDOWN <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })
        )}
      </Section>

      <GameAnalysisModal
        open={!!analysisGame}
        onOpenChange={(open) => { if (!open) setAnalysisGame(null); }}
        game={analysisGame ? { ...analysisGame.row } : {}}
        ats={ats}
        opponentName={analysisGame?.game?.opponent_name || 'Opponent'}
        gameDate={analysisGame?.game?.date_time
          ? new Date(analysisGame.game.date_time).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
          : ''}
      />

      {/* ── Linked Players modal ── */}
      <Dialog open={linksModalOpen} onOpenChange={setLinksModalOpen}>
        <DialogContent className="max-w-md" data-testid="player-links-modal">
          <DialogHeader>
            <DialogTitle
              className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Linked Players
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              Link another coach&apos;s player code to combine all their stats
              into this profile. Linking is permanent until you disconnect it.
            </DialogDescription>
          </DialogHeader>

          {/* Existing links */}
          <div className="mt-2 space-y-2" data-testid="player-links-list">
            {links.length === 0 ? (
              <p className="text-[13px] py-3 text-center" style={{ color: MUTED }}>
                No linked players yet.
              </p>
            ) : (
              links.map(lp => (
                <button
                  key={lp.player_id}
                  type="button"
                  onClick={() => setUnlinkTarget(lp)}
                  data-testid={`linked-player-${lp.player_id}`}
                  className="w-full rounded-xl p-3 flex items-center gap-3 transition-all hover:bg-slate-50 active:scale-[.99]"
                  style={{ background: '#fff', border: `1.5px solid ${BORDER}` }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 16 }}>
                    {lp.jersey_number || '–'}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-[15px] font-bold truncate leading-tight" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {lp.name || 'Linked Player'}
                    </p>
                    <p className="text-[11px] mt-0.5 truncate uppercase tracking-[.08em]" style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {lp.team_name || '—'} · {lp.link_code}
                    </p>
                  </div>
                  <X className="w-4 h-4 shrink-0" style={{ color: MUTED }} />
                </button>
              ))
            )}
          </div>

          {/* Add by code */}
          <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${BORDER}` }}>
            <p
              className="text-[11px] font-bold uppercase tracking-[.12em] mb-2"
              style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Add a player by code
            </p>
            <div className="flex gap-2">
              <Input
                value={linkingCode}
                onChange={(e) => setLinkingCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                placeholder="ABCD1234"
                maxLength={8}
                data-testid="player-link-code-input"
                className="tracking-[0.32em] uppercase font-mono text-center text-base"
              />
              <Button
                type="button"
                onClick={submitLinkCode}
                disabled={linking || linkingCode.length !== 8}
                data-testid="player-link-submit"
                className="h-10 px-4 rounded-full font-bold uppercase"
                style={{ background: NAVY, color: '#fff' }}
              >
                <Plus className="w-4 h-4 mr-1" /> Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Disconnect confirmation modal ── */}
      <Dialog open={!!unlinkTarget} onOpenChange={(open) => { if (!open) setUnlinkTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="player-unlink-modal">
          <DialogHeader>
            <DialogTitle
              className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Disconnect Link?
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              {unlinkTarget?.name || 'This player'}{unlinkTarget?.team_name ? ` (${unlinkTarget.team_name})` : ''} will no longer combine into this profile&apos;s stats. You can re-link with the same code anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setUnlinkTarget(null)}
              className="flex-1 h-11 rounded-full"
              data-testid="player-unlink-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmUnlink}
              data-testid="player-unlink-confirm"
              className="flex-1 h-11 rounded-full font-bold uppercase"
              style={{ background: '#dc2626', color: '#fff' }}
            >
              Disconnect
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── small subcomponents ── */
function Section({ title, children, testid }) {
  return (
    <div className="px-4 py-4 mb-2" style={{ background: '#fff' }} data-testid={testid}>
      <div className="text-[13px] mb-3"
        style={{ color: 'rgba(0,0,0,.4)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.10em' }}>
        {title.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

function Row5({ cells }) {
  return (
    <div className="grid grid-cols-5 gap-1">
      {cells.map(c => (
        <div key={c.label} className="text-center">
          {c.val == null ? (
            <div className="text-[20px] leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: 'rgba(0,0,0,.25)' }}>
              NA
            </div>
          ) : (
            <div className="text-[28px] leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: c.highlight ? ORANGE : NAVY }}>
              {c.val}
            </div>
          )}
          <div className="text-[12px] mt-1.5" style={{ color: 'rgba(0,0,0,.4)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.06em' }}>
            {c.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function SplitTile({ label, pct, color }) {
  const isNA = pct == null;
  return (
    <div className="rounded-[12px] py-4 px-2 text-center"
      style={{ background: '#F1F5F9' }}
      data-testid={`split-${label.toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
      {isNA ? (
        <div className="text-[24px] leading-none"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: 'rgba(0,0,0,.2)' }}>
          NA
        </div>
      ) : (
        <div className="text-[34px] leading-none"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: color || NAVY }}>
          {pct}%
        </div>
      )}
      <div className="text-[12px] mt-2" style={{ color: 'rgba(0,0,0,.4)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.08em' }}>
        {label}
      </div>
    </div>
  );
}
